// @ts-nocheck
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { getSessionUser } from '@/lib/authSession';

export interface AudienceSegment {
  id: string;
  segment_name: string;
  description: string | null;
  gender: string[] | null;
  sports: string[] | null;
  grad_year_min: number | null;
  grad_year_max: number | null;
  states: string[] | null;
  membership_types: string[] | null;
  explicit_profile_ids: string[] | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  estimated_recipients: number | null;
  last_count_updated: string | null;
  audience_type: string | null;
  coach_roles: string[] | null;
}

export const useAudienceSegments = () => {
  const [segments, setSegments] = useState<AudienceSegment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const fetchSegments = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("email_audience_segments")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setSegments(data || []);
    } catch (error: any) {
      console.error("Error fetching segments:", error);
      toast({
        title: "Error",
        description: "Failed to load audience segments.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const createSegment = async (segmentData: Omit<AudienceSegment, "id" | "created_at" | "updated_at" | "estimated_recipients" | "last_count_updated" | "created_by">) => {
    try {
      const { data: { user } } = await getSessionUser();
      
      const { data, error } = await supabase
        .from("email_audience_segments")
        .insert([{
          ...segmentData,
          created_by: user?.id,
        }])
        .select()
        .single();

      if (error) throw error;
      
      await fetchSegments();
      return data;
    } catch (error: any) {
      console.error("Error creating segment:", error);
      throw error;
    }
  };

  const updateSegment = async (id: string, updates: Partial<AudienceSegment>) => {
    try {
      const { error } = await supabase
        .from("email_audience_segments")
        .update(updates)
        .eq("id", id);

      if (error) throw error;
      
      await fetchSegments();
    } catch (error: any) {
      console.error("Error updating segment:", error);
      throw error;
    }
  };

  const deleteSegment = async (id: string) => {
    try {
      const { error } = await supabase
        .from("email_audience_segments")
        .delete()
        .eq("id", id);

      if (error) throw error;
      
      await fetchSegments();
    } catch (error: any) {
      console.error("Error deleting segment:", error);
      throw error;
    }
  };

  const calculateRecipients = async (filters: {
    gender?: string[];
    sports?: string[];
    grad_year_min?: number;
    grad_year_max?: number;
    states?: string[];
    membership_types?: string[];
    audience_type?: string;
  }) => {
    try {
      console.log("Calculating recipients with filters:", filters);
      
      // Determine the role to filter by based on audience_type
      const role = filters.audience_type === "coach" ? "coach" : "athlete";
      
      let query = supabase
        .from("profiles")
        .select("id", { count: "exact", head: true })
        .eq("role", role);

      if (filters.gender?.length) {
        // Map "Men" to ["Men", "Male"] and "Women" to ["Women", "Female"]
        const mappedGenders: string[] = [];
        filters.gender.forEach(g => {
          if (g === "Men") {
            mappedGenders.push("Men", "Male");
          } else if (g === "Women") {
            mappedGenders.push("Women", "Female");
          } else {
            mappedGenders.push(g);
          }
        });
        console.log("Filtering by gender:", mappedGenders);
        query = query.or(`gender.in.(${mappedGenders.join(',')}),gender.is.null`);
      }

      if (filters.sports?.length) {
        console.log("Filtering by sport_id:", filters.sports);
        query = query.in("sport_id", filters.sports);
      }

      if (filters.grad_year_min) {
        console.log("Filtering grad_year >=", filters.grad_year_min);
        query = query.gte("grad_year", filters.grad_year_min);
      }

      if (filters.grad_year_max) {
        console.log("Filtering grad_year <=", filters.grad_year_max);
        query = query.lte("grad_year", filters.grad_year_max);
      }

      if (filters.states?.length) {
        console.log("Filtering by state:", filters.states);
        query = query.in("state", filters.states);
      }

      if (filters.membership_types?.length) {
        console.log("Filtering by membership:", filters.membership_types);
        query = query.in("active_membership_slug", filters.membership_types);
      }

      const { count, error } = await query;

      if (error) {
        console.error("Query error:", error);
        throw error;
      }
      
      console.log("Recipients found:", count);
      return count || 0;
    } catch (error: any) {
      console.error("Error calculating recipients:", error);
      throw error;
    }
  };

  const refreshSegmentCount = async (segment: AudienceSegment) => {
    try {
      // ALWAYS calculate based on filters - NEVER use explicit_profile_ids
      let query = supabase
        .from("profiles")
        .select("id", { count: "exact", head: true })
        .eq("role", "athlete");

      if (segment.sports?.length) {
        query = query.in("sport_id", segment.sports);
      }

      if (segment.gender?.length) {
        // Map "Men" to ["Men", "Male"] and "Women" to ["Women", "Female"]
        const mappedGenders: string[] = [];
        segment.gender.forEach(g => {
          if (g === "Men") {
            mappedGenders.push("Men", "Male");
          } else if (g === "Women") {
            mappedGenders.push("Women", "Female");
          } else {
            mappedGenders.push(g);
          }
        });
        query = query.or(`gender.in.(${mappedGenders.join(',')}),gender.is.null`);
      }

      if (segment.grad_year_min) {
        query = query.gte("grad_year", segment.grad_year_min);
      }

      if (segment.grad_year_max) {
        query = query.lte("grad_year", segment.grad_year_max);
      }

      if (segment.states?.length) {
        query = query.in("state", segment.states);
      }

      if (segment.membership_types?.length) {
        query = query.in("active_membership_slug", segment.membership_types);
      }

      const { count, error } = await query;
      if (error) throw error;
      const finalCount = count || 0;

      // Update the segment with the new count
      await updateSegment(segment.id, {
        estimated_recipients: finalCount,
        last_count_updated: new Date().toISOString(),
      });

      await fetchSegments();
      return finalCount;
    } catch (error: any) {
      console.error("Error refreshing segment count:", error);
      throw error;
    }
  };

  useEffect(() => {
    fetchSegments();
  }, []);

  return {
    segments,
    isLoading,
    createSegment,
    updateSegment,
    deleteSegment,
    calculateRecipients,
    refreshSegmentCount,
    refetch: fetchSegments,
  };
};
